import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { isEqual } from "lodash";

import { useAppDispatch, useAppSelector } from "app/hooks";
import CircularProgress from "components/circular-progress";
import FeatureIsNotAvailable from "components/feature-is-not-available/feature-is-not-available.component";
import { selectCurrentUser } from "features/auth/auth-slice";
import useAuth from "hooks/use-authorize.hook";
import { useCreateJwtTokenMutation } from "services/suno-comms/suno-comms-api";
import {
  leadMessagesApi,
  useGetUnreadMessagesCountQuery
} from "services/suno-comms/suno-comms-messages-api";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";
import { viteEnv } from "utils/vite-env.util";

import { LEADS_UI } from "../../constants/feature-flags";
import {
  logoutSunoComms,
  selectCommsAuthorized,
  setAuthTokenInfo
} from "./leads-ui.slice";
import type {
  CommsNotification,
  CommsNotificationMessage
} from "./types/leads-values.type";

type MessageHandler = (message: unknown) => void;

type LeadsContextData = {
  isLoading?: boolean;
  isEnabled?: boolean;
  authorized?: boolean;
  lastNotification?: CommsNotification;
  addMessageHandlers?: (handler: MessageHandler, key: string) => void;
  removeMessageHandlers?: (key: string) => void;
  messages?: CommsNotification[];
  setMessages: React.Dispatch<React.SetStateAction<CommsNotification[]>>;
  unreadMessagesCount?: number | undefined;
  previewEmailId?: string | undefined;
  setPreviewEmailId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  convertContactIdIntoPatient?: string | undefined;
  setConvertContactIdIntoPatient?: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  removeNotification?: (id: string) => void;
  readNotification?: (id: string) => void;
  readNotifications?: (ids: string[]) => void;
  readAllNotifications?: () => void;
  refetchUnreadMessagesCount?: () => void;
};
const LeadsContext = React.createContext<LeadsContextData | null>(null);

type ProviderProps = {
  children?: React.ReactNode;
};

const clinicNotificationsStorage = setupLocalStorageAPI("comms-notifications");

const JWT_RETRY_DELAY_MS = 2500;
const JWT_MAX_RETRIES = 2;

// Max time to wait for LaunchDarkly to identify the real user before falling back
// to whatever flag value we have. Prevents an indefinite spinner if LD never
// initializes or identify rejects.
const LD_READY_TIMEOUT_MS = 15000;

export function LeadsProvider({ children }: ProviderProps) {
  useAuth();
  const flags = useFlags();
  const dispatch = useAppDispatch();
  const isLeadsUiEnabled = flags[LEADS_UI];

  const ldClient = useLDClient();
  const currentUser = useAppSelector(selectCurrentUser);
  const authorized = useAppSelector(selectCommsAuthorized);

  const [previewEmailId, setPreviewEmailId] = useState<string | undefined>();
  const [convertContactIdIntoPatient, setConvertContactIdIntoPatient] =
    useState<string | undefined>();

  const defaultNotifications = clinicNotificationsStorage.getData([]);
  const [notifications, setNotifications] =
    useState<CommsNotificationMessage[]>(defaultNotifications);

  const [messages, setMessages] = useState<CommsNotification[]>([]);
  const [lastProcessedMessage, setLastProcessedMessage] = useState<any>(null);

  const onMessageReceivedHandlers = useRef<Record<string, MessageHandler>>({});

  const [socketUrl, setSocketUrl] = useState("");

  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket<any>(
    socketUrl,
    { shouldReconnect: () => true },
    Boolean(socketUrl)
  );

  const [createJwtToken, { isLoading: isAuthInProgress }] =
    useCreateJwtTokenMutation();

  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const retryCountRef = useRef(0);
  const [retryTick, setRetryTick] = useState(0);
  const [jwtFailed, setJwtFailed] = useState(false);

  const isUserLoaded = Boolean(currentUser?.id && currentUser.id !== -1);

  // Wait for LaunchDarkly to identify the real user before trusting the flag — until
  // then `flags[LEADS_UI]` is the anonymous-context default (false), which flashes the
  // "feature not available" message on slow networks. Ask the client which context it
  // has identified rather than the redux `ldIdentified` flag, which can be set
  // optimistically before a real identify completes.
  const ldContext = ldClient?.getContext?.();
  const isLdReadyForUser =
    !!ldContext &&
    "key" in ldContext &&
    ldContext.key === String(currentUser.id);

  // Fallback so the spinner never hangs if LD never identifies the user.
  const [ldReadyTimedOut, setLdReadyTimedOut] = useState(false);
  useEffect(() => {
    if (!isUserLoaded || isLdReadyForUser) return;
    const timer = setTimeout(
      () => setLdReadyTimedOut(true),
      LD_READY_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [isUserLoaded, isLdReadyForUser]);

  const isLdReady = isLdReadyForUser || ldReadyTimedOut;

  const isDataReady = isUserLoaded && isLdReady;

  const isFeatureDisabled =
    isDataReady && (isLeadsUiEnabled === false || !currentUser?.comms_api_key);

  const { data: unreadMessagesCount, refetch: refetchUnreadMessagesCount } =
    useGetUnreadMessagesCountQuery(undefined, {
      pollingInterval: 3600000,
      skip: !authorized
    });

  useEffect(() => {
    if (isFeatureDisabled) {
      dispatch(logoutSunoComms());
    }
  }, [isFeatureDisabled, dispatch]);

  useEffect(() => {
    if (
      isDataReady &&
      !isFeatureDisabled &&
      !jwtFailed &&
      !authorized &&
      !isAuthInProgress &&
      currentUser?.comms_api_key
    ) {
      createJwtToken(String(currentUser.comms_api_key)).then((response) => {
        if ("error" in response || !response?.data?.access_token) {
          if (retryCountRef.current < JWT_MAX_RETRIES) {
            retryCountRef.current += 1;
            retryTimeoutRef.current = setTimeout(() => {
              setRetryTick((t) => t + 1);
            }, JWT_RETRY_DELAY_MS);
          } else {
            setJwtFailed(true);
            dispatch(logoutSunoComms());
          }
          return;
        }

        retryCountRef.current = 0;
        dispatch(
          setAuthTokenInfo({
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token,
            access_token_expires_at: response.data.expires_at
          })
        );
      });
    }
  }, [
    authorized,
    currentUser?.comms_api_key,
    isDataReady,
    isFeatureDisabled,
    isAuthInProgress,
    jwtFailed,
    createJwtToken,
    dispatch,
    retryTick
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (currentUser.id && currentUser.id !== -1 && authorized) {
      setSocketUrl(`${viteEnv.VITE_SUNO_COMMS_NOTIFICATIONS_WS_URL}`);
    }
  }, [authorized, currentUser.id]);

  useEffect(() => {
    if (
      readyState === ReadyState.OPEN &&
      socketUrl &&
      currentUser?.comms_api_key
    ) {
      sendJsonMessage({
        api_key: currentUser?.comms_api_key,
        role: "user"
      });
    }
  }, [currentUser?.comms_api_key, readyState, sendJsonMessage, socketUrl]);

  useEffect(() => {
    if (!isEqual(lastJsonMessage, lastProcessedMessage)) {
      Object.values(onMessageReceivedHandlers.current).map(
        (handler) => handler && handler(lastJsonMessage)
      );
    }
  }, [lastJsonMessage, onMessageReceivedHandlers, lastProcessedMessage]);

  //
  useEffect(() => {
    if (lastJsonMessage !== null) {
      if (lastJsonMessage.type === "notify_message") {
        setMessages((prev) =>
          prev.concat({
            type: lastJsonMessage.type,
            message: lastJsonMessage.message,
            is_read: false
          })
        );
        if (lastJsonMessage.message.is_read === false) {
          const current = clinicNotificationsStorage.getData([]);
          clinicNotificationsStorage.updateData([
            ...current,
            lastJsonMessage.message
          ]);
          setNotifications((prev) => prev.concat(lastJsonMessage.message));
        }
        // Invalidate unread messages count to trigger refetch
        dispatch(leadMessagesApi.util.invalidateTags(["LeadMessagesCount"]));
      }
      setLastProcessedMessage(lastJsonMessage);
    }
  }, [dispatch, lastJsonMessage, setLastProcessedMessage]);

  const removeNotification = useCallback(
    (id: string) => {
      const index = notifications.findIndex(
        (notification) => notification.id === id
      );
      if (index !== -1) {
        const newNotifications = [...notifications];
        newNotifications.splice(index, 1);
        setNotifications(newNotifications);
        clinicNotificationsStorage.updateData(newNotifications);
      }
    },
    [notifications]
  );
  const readNotification = useCallback(
    (id: string) => {
      const newNotifications = notifications.map((notification) => ({
        ...notification,
        is_read: notification.id === id ? true : notification.is_read
      }));
      setNotifications(newNotifications);
      clinicNotificationsStorage.updateData(newNotifications);
    },
    [notifications]
  );
  const readNotifications = useCallback(
    (ids: string[]) => {
      const newNotifications = notifications.map((notification) => ({
        ...notification,
        is_read: ids.includes(notification.id) ? true : notification.is_read
      }));
      setNotifications(newNotifications);
      clinicNotificationsStorage.updateData(newNotifications);
    },
    [notifications]
  );
  const readAllNotifications = useCallback(() => {
    const newNotifications = notifications.map((notification) => ({
      ...notification,
      is_read: true
    }));
    setNotifications(newNotifications);
    clinicNotificationsStorage.updateData(newNotifications);
  }, [notifications]);

  const addMessageHandlers = useCallback(
    (handler: MessageHandler, key: string) => {
      onMessageReceivedHandlers.current[key] = handler;
    },
    [onMessageReceivedHandlers]
  );

  const removeMessageHandlers = useCallback((key: string) => {
    delete onMessageReceivedHandlers.current[key];
  }, []);

  const isInitializing = !isDataReady;
  const isAuthenticating =
    isDataReady && !isFeatureDisabled && !jwtFailed && !authorized;

  const value = useMemo(() => {
    return {
      lastNotification: lastJsonMessage,
      isLoading: isInitializing || isAuthenticating,
      isEnabled:
        isDataReady && !isFeatureDisabled && !jwtFailed && Boolean(authorized),
      unreadMessagesCount,
      messages,
      previewEmailId,
      setPreviewEmailId,
      convertContactIdIntoPatient,
      setConvertContactIdIntoPatient,
      addMessageHandlers,
      removeMessageHandlers,
      removeNotification,
      readNotification,
      readNotifications,
      readAllNotifications,
      refetchUnreadMessagesCount,
      setMessages
    };
  }, [
    lastJsonMessage,
    isInitializing,
    isAuthenticating,
    isDataReady,
    isFeatureDisabled,
    jwtFailed,
    authorized,
    unreadMessagesCount,
    messages,
    previewEmailId,
    convertContactIdIntoPatient,
    addMessageHandlers,
    removeMessageHandlers,
    removeNotification,
    readNotification,
    readNotifications,
    readAllNotifications,
    refetchUnreadMessagesCount,
    setMessages
  ]);

  if (isInitializing || isAuthenticating) {
    return <CircularProgress />;
  }

  if (isFeatureDisabled || jwtFailed) {
    return <FeatureIsNotAvailable featureName="Leads feature" />;
  }

  return (
    <LeadsContext.Provider value={value}>{children}</LeadsContext.Provider>
  );
}

export default function useLeads(): LeadsContextData {
  const context = React.useContext(LeadsContext);
  return context || ({} as LeadsContextData);
}
